import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useProcessApiClient } from '@/api/clients/useProcessApiClient.util.ts';
import { CreateProcessRequestDto, ProcessCreationResponseDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { PROCESS_CUSTOMER_ERROR_CODES } from '@/views/customers/status/processCustomerErrorCodes.model.ts';
import { useCustomerErrorToast } from '@/views/customers/status/useCustomerErrorToast.util.ts';

type Req = CreateProcessRequestDto;

type Response = ProcessCreationResponseDto;

type Return = {
  createProcess: (req: Req) => Promise<Response>;
  isCreatingProcess: boolean;
};

export const useCreateProcess = (): Return => {
  const { processesApi } = useProcessApiClient();
  const queryClient = useQueryClient();
  const { handleError, meta } = useCustomerErrorToast(PROCESS_CUSTOMER_ERROR_CODES);

  const { isPending, mutateAsync } = useMutation<Response, AxiosError, Req>({
    meta: { ...meta, disableSuccessAlert: true },
    mutationFn: async req => {
      const res = await processesApi.createProcess(req);

      return res.data;
    },
    mutationKey: [QueryKeyEnum.PROCESS.CREATE],
    onError: handleError,
    onSuccess: data => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.PROCESS.DETAILS, data.processId],
      });
      void queryClient.invalidateQueries({ queryKey: [QueryKeyEnum.PROCESSES.SEARCH] });
      // the process may request a calendar entry, so the calendar stays current wherever it is created
      void queryClient.invalidateQueries({ queryKey: [QueryKeyEnum.DICTIONARIES.AVAILABILITY] });
    },
  });

  return {
    createProcess: mutateAsync,
    isCreatingProcess: isPending,
  };
};
